/**
 * Skills Command
 * Manage Claude Code-style skills from .metis/skills/
 */

import kleur from 'kleur';
import fs from 'fs';
import path from 'path';
import { getSkillLoader, LoadedSkill } from '../../skills';

export async function runSkills(args: string[]): Promise<void> {
  const skillLoader = getSkillLoader();
  const subCommand = args[0] || 'list';

  switch (subCommand) {
    case 'list':
    case 'ls':
      listSkills();
      break;

    case 'show':
      if (args[1]) {
        showSkill(args[1]);
      } else {
        console.log(kleur.yellow('Usage: /skills show <skill-name>'));
      }
      break;

    case 'create':
      if (args[1]) {
        await createSkill(args[1], args.includes('--global'));
      } else {
        console.log(kleur.yellow('Usage: /skills create <name> [--global]'));
      }
      break;

    case 'delete':
      if (args[1]) {
        deleteSkill(args[1], args.includes('--global'));
      } else {
        console.log(kleur.yellow('Usage: /skills delete <name> [--global]'));
      }
      break;

    case 'refresh':
      refreshSkills();
      break;

    case 'test':
      if (args[1]) {
        testSkillMatching(args.slice(1).join(' '));
      } else {
        console.log(kleur.yellow('Usage: /skills test <prompt>'));
      }
      break;

    case 'help':
    default:
      showHelp();
  }
}

function listSkills(): void {
  const skillLoader = getSkillLoader();
  const skills = skillLoader.getAllSkills();

  console.log(kleur.cyan('\n🎯 Skills'));
  console.log(kleur.gray('─'.repeat(60)));

  if (skills.length === 0) {
    console.log(kleur.gray('No skills installed'));
    console.log();
    console.log(kleur.white('Create skills in:'));
    console.log(kleur.gray('  ~/.metis/skills/<skill-name>/SKILL.md  - Global skills'));
    console.log(kleur.gray('  .metis/skills/<skill-name>/SKILL.md   - Project skills'));
    console.log();
    console.log(kleur.white('Or use: /skills create <name>'));
    return;
  }

  console.log(kleur.gray(`Total: ${skills.length} skill(s)`));
  console.log();

  // Group by level
  const globalSkills = skills.filter(s => s.level === 'global');
  const projectSkills = skills.filter(s => s.level === 'project');

  if (globalSkills.length > 0) {
    console.log(kleur.cyan('Global Skills (~/.metis/skills/):'));
    for (const skill of globalSkills) {
      printSkillSummary(skill);
    }
    console.log();
  }

  if (projectSkills.length > 0) {
    console.log(kleur.cyan('Project Skills (.metis/skills/):'));
    for (const skill of projectSkills) {
      printSkillSummary(skill);
    }
    console.log();
  }

  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /skills show <name>  - View skill details'));
  console.log(kleur.gray('  /skills create <name> - Create new skill'));
  console.log(kleur.gray('  /skills test <prompt> - Test skill matching'));
}

function printSkillSummary(skill: LoadedSkill): void {
  const tags = skill.metadata.tags?.length
    ? kleur.gray(` [${skill.metadata.tags.join(', ')}]`)
    : '';

  console.log(kleur.yellow(`  🎯 ${skill.metadata.name}`) + tags);
  console.log(kleur.gray(`     ${skill.metadata.description}`));

  const extras: string[] = [];
  if (skill.scripts.length > 0) {
    extras.push(`${skill.scripts.length} script(s)`);
  }
  if (skill.resources.length > 0) {
    extras.push(`${skill.resources.length} resource(s)`);
  }
  if (skill.reference) {
    extras.push('has reference');
  }

  if (extras.length > 0) {
    console.log(kleur.gray(`     ${extras.join(' | ')}`));
  }
}

function showSkill(name: string): void {
  const skillLoader = getSkillLoader();
  const skill = skillLoader.getSkill(name);

  if (!skill) {
    // Try case-insensitive match
    const skills = skillLoader.getAllSkills();
    const match = skills.find(s =>
      s.metadata.name.toLowerCase() === name.toLowerCase()
    );

    if (match) {
      showSkillDetails(match);
      return;
    }

    console.log(kleur.red(`Skill not found: ${name}`));
    console.log(kleur.gray('Use /skills list to see available skills'));
    return;
  }

  showSkillDetails(skill);
}

function showSkillDetails(skill: LoadedSkill): void {
  console.log(kleur.cyan(`\n🎯 ${skill.metadata.name}`));
  console.log(kleur.gray('─'.repeat(60)));

  console.log(kleur.white('Description: ') + skill.metadata.description);
  console.log(kleur.white('Level: ') + kleur.yellow(skill.level));
  console.log(kleur.white('Path: ') + kleur.gray(skill.path));

  if (skill.metadata.version) {
    console.log(kleur.white('Version: ') + skill.metadata.version);
  }

  if (skill.metadata.author) {
    console.log(kleur.white('Author: ') + skill.metadata.author);
  }

  if (skill.metadata.tags && skill.metadata.tags.length > 0) {
    console.log(kleur.white('Tags: ') + skill.metadata.tags.join(', '));
  }

  if (skill.metadata.dependencies && skill.metadata.dependencies.length > 0) {
    console.log(kleur.white('Dependencies: ') + skill.metadata.dependencies.join(', '));
  }

  console.log();
  console.log(kleur.cyan('Instructions:'));
  console.log(kleur.gray('─'.repeat(40)));

  // Show first 20 lines
  const lines = skill.instructions.split('\n');
  const preview = lines.slice(0, 20).join('\n');
  console.log(preview);

  if (lines.length > 20) {
    console.log();
    console.log(kleur.gray(`... and ${lines.length - 20} more lines`));
  }

  if (skill.scripts.length > 0) {
    console.log();
    console.log(kleur.cyan('Scripts:'));
    for (const script of skill.scripts) {
      console.log(kleur.gray(`  - ${script}`));
    }
  }

  if (skill.resources.length > 0) {
    console.log();
    console.log(kleur.cyan('Resources:'));
    for (const resource of skill.resources) {
      console.log(kleur.gray(`  - ${resource}`));
    }
  }

  if (skill.reference) {
    console.log();
    console.log(kleur.cyan('Reference:'));
    const refLines = skill.reference.split('\n').slice(0, 10);
    console.log(kleur.gray(refLines.join('\n')));
    if (skill.reference.split('\n').length > 10) {
      console.log(kleur.gray('...'));
    }
  }
}

async function createSkill(name: string, global: boolean): Promise<void> {
  const skillLoader = getSkillLoader();

  // Check if skill already exists
  const existing = skillLoader.getSkill(name);
  if (existing) {
    console.log(kleur.yellow(`Skill '${name}' already exists at ${existing.path}`));
    return;
  }

  // Create template
  const template = `# ${formatSkillName(name)}

## When to Use

Describe when this skill should be activated.

## Instructions

1. First instruction
2. Second instruction
3. Third instruction

## Guidelines

- Guideline 1
- Guideline 2

## Examples

\`\`\`
Add examples here
\`\`\`
`;

  try {
    const skillPath = skillLoader.createSkill(
      name,
      `Description for ${name} skill`,
      template,
      global
    );

    console.log(kleur.green(`✅ Skill created: ${name}`));
    console.log(kleur.gray(`Path: ${skillPath}`));
    console.log();
    console.log(kleur.cyan('Next steps:'));
    console.log(kleur.gray(`  1. Edit ${path.join(skillPath, 'SKILL.md')}`));
    console.log(kleur.gray('  2. Update the description in frontmatter'));
    console.log(kleur.gray('  3. Add tags for better matching'));
    console.log(kleur.gray('  4. Write clear instructions'));
    console.log();
    console.log(kleur.gray('Use /skills refresh after editing'));
  } catch (error) {
    console.log(kleur.red(`Failed to create skill: ${error}`));
  }
}

function deleteSkill(name: string, global: boolean): void {
  const skillLoader = getSkillLoader();

  const success = skillLoader.deleteSkill(name, global);

  if (success) {
    console.log(kleur.green(`✅ Skill deleted: ${name}`));
  } else {
    console.log(kleur.red(`Skill not found: ${name}`));
    console.log(kleur.gray(`Location checked: ${global ? '~/.metis/skills/' : '.metis/skills/'}`));
  }
}

function refreshSkills(): void {
  const skillLoader = getSkillLoader();
  skillLoader.clearCache();
  const skills = skillLoader.getAllSkills();

  console.log(kleur.green('✅ Skills refreshed'));
  console.log(kleur.gray(`Loaded ${skills.length} skill(s)`));
}

function testSkillMatching(prompt: string): void {
  const skillLoader = getSkillLoader();

  console.log(kleur.cyan('\n🔍 Testing Skill Matching'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.white('Prompt: ') + prompt);
  console.log();

  const relevant = skillLoader.findRelevantSkills({
    prompt,
    cwd: process.cwd(),
  });

  if (relevant.length === 0) {
    console.log(kleur.yellow('No skills matched this prompt'));
    console.log(kleur.gray('Skills are matched by name, tags, and description keywords'));
    return;
  }

  console.log(kleur.green(`Found ${relevant.length} relevant skill(s):`));
  console.log();

  for (const skill of relevant) {
    console.log(kleur.yellow(`  ✓ ${skill.metadata.name}`));
    console.log(kleur.gray(`    ${skill.metadata.description}`));
    if (skill.metadata.tags?.length) {
      console.log(kleur.gray(`    Tags: ${skill.metadata.tags.join(', ')}`));
    }
  }
}

function showHelp(): void {
  console.log(kleur.cyan('\n🎯 Skills Command'));
  console.log(kleur.gray('─'.repeat(60)));
  console.log(kleur.white('Manage Claude Code-style skills'));
  console.log();
  console.log(kleur.cyan('About Skills:'));
  console.log(kleur.gray('  Skills are folders with SKILL.md files containing instructions'));
  console.log(kleur.gray('  Claude auto-invokes relevant skills based on your task'));
  console.log(kleur.gray('  Skills can include scripts, reference docs, and resources'));
  console.log();
  console.log(kleur.cyan('Skill Locations:'));
  console.log(kleur.gray('  ~/.metis/skills/<name>/SKILL.md  - Global skills'));
  console.log(kleur.gray('  .metis/skills/<name>/SKILL.md    - Project skills'));
  console.log();
  console.log(kleur.cyan('SKILL.md Format:'));
  console.log(kleur.gray('  ---'));
  console.log(kleur.gray('  name: my-skill'));
  console.log(kleur.gray('  description: What this skill does'));
  console.log(kleur.gray('  tags: [python, testing]'));
  console.log(kleur.gray('  ---'));
  console.log(kleur.gray('  # Instructions for the AI...'));
  console.log();
  console.log(kleur.cyan('Commands:'));
  console.log(kleur.gray('  /skills              - List all skills'));
  console.log(kleur.gray('  /skills list         - List all skills'));
  console.log(kleur.gray('  /skills show <name>  - View skill details'));
  console.log(kleur.gray('  /skills create <name> [--global] - Create new skill'));
  console.log(kleur.gray('  /skills delete <name> [--global] - Delete skill'));
  console.log(kleur.gray('  /skills refresh      - Reload skills from disk'));
  console.log(kleur.gray('  /skills test <prompt> - Test which skills match a prompt'));
}

function formatSkillName(name: string): string {
  return name
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}
